import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {BreadcrumbModel} from '@shared/models/breadcrumb.model';
import {ActivatedRoute, Router} from '@angular/router';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {Configuration} from '@shared/models/configuration';
import { FuseSplashScreenService } from '@barebone-fuse-services/splash-screen.service';
import {Store} from '@ngrx/store';
import {IVirtualGridCollectionState} from '@shared/store/reducers/virtual-grid.reducer';
import {Observable} from 'rxjs';
import {
    GetFieldSectionsAction,
    ResetVirtualGridAction,
} from '@shared/store/actions/virtual-grid.actions';
import {FxRateState} from '../../store/reducers/fx-rate.reducer';
import {GetFxRateReportAction} from '../../store/actions/fx-rate.action';
import {FxRateReportModel} from '../../models/fx-rate-report.model';
import {getReport} from '../../store/selectors/fx-rate.selectors';
import {FieldSection} from '@shared/models/field-section';
import {Subject} from 'rxjs';
import {OnDestroy} from '@angular/core';
import {
    ChangeSummaryHeaderCommand, DeleteDynamicFieldCommand,
    ExportReportCommand
} from '../../commands/fx-rate-report.command';
import {CommandService} from '@core/services/command.service';
import {FileManagementService} from '@core/services/file-management.service';
import {DatePipe} from '@angular/common';
import {FxRateRateType} from '../../constants/fx-rate-constants';
import { MatIconRegistry } from '@angular/material/icon';
import {DomSanitizer} from '@angular/platform-browser';
import {takeUntil} from 'rxjs/operators';
import {SetActivityFeedAction} from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import {PlatformSidebarState} from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';
import {TimeZoneService} from '@core/services/time-zone.service';

@Component({
    standalone: false,
    selector: 'particle-fx-rate-view-report',
    templateUrl: './fx-rate-view-report.component.html',
    styleUrls: ['./fx-rate-view-report.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FxRateViewReportComponent implements OnInit, OnDestroy {
    breadcrumbs: BreadcrumbModel[];
    reportName: string;
    reportId: string;
    showNote: boolean = false;
    invalidateSubject: InvalidateSubject;
    sourceType = 'FxRateReportDetails';
    newSourceType: string;
    fieldSections$: Observable<FieldSection[]>;
    reportingCurrencies: string;
    translationCurrencies: string;
    reportModel: FxRateReportModel = null;
    reportModel$: Observable<FxRateReportModel>;
    hasSummaryData: boolean = false;
    hasGeneralData: boolean = false;
    unsubscribe$ = new Subject<void>();

    constructor(private route: ActivatedRoute,
                iconRegistry: MatIconRegistry,
                sanitizer: DomSanitizer,
                private store: Store<IVirtualGridCollectionState>,
                private fuseSplashScreenService: FuseSplashScreenService,
                private datepipe: DatePipe,
                private router: Router,
                private fxratestore: Store<FxRateState>,
                private commandService: CommandService,
                private platformSidebarStore: Store<PlatformSidebarState>,
                private fileManagementService: FileManagementService,
                private timeZoneService: TimeZoneService) {
        this.route.params.subscribe(params => {
            if (params['reportName']) {
                this.reportName = params['reportName']; // the router already decodes route params once
            }
            if (params['reportID']) {
                this.reportId = params['reportID'];
                this.fxratestore.dispatch(new GetFxRateReportAction(this.reportId));
            }
        });
        this.newSourceType = this.sourceType + '-' + this.reportId;
        this.invalidateSubject = new InvalidateSubject();
        this.breadcrumbs = [
            <BreadcrumbModel>{
                id: 'fx-rates',
                title: 'FX Rates',
                type: '',
                relativeUrl: 'itrce/fxrates'
            },
            <BreadcrumbModel>{
                id: 'view-report',
                title: this.reportName,
                type: '',
                // Breadcrumbs navigate by URL string, so let the router serialize the segments: it escapes every
                // character its parser treats specially ("(", ")", ";", "%", ...) instead of a hand-picked list.
                relativeUrl: this.router.serializeUrl(this.router.createUrlTree(
                    ['itrce', 'fxrates', 'fx-rates-view-report', this.reportName, this.reportId]))
            }
        ];

        iconRegistry.addSvgIcon(
            'edit-icon',
            sanitizer.bypassSecurityTrustResourceUrl('http://s3-us-west-2.amazonaws.com/orbitaxservices.com/orbitax-assets/icons/svgs/edit-icon.svg'));
    }

    ngOnInit() {
        this.invalidate('Simple Average');
        this.fxratestore.dispatch(new GetFxRateReportAction(this.reportId));
        this.reportModel$ = this.fxratestore.select(getReport(this.reportId));
        this.reportModel$.pipe(takeUntil(this.unsubscribe$)).subscribe((result: any) => {
            if (result) {
                this.reportModel = result;
                if (this.reportModel) {
                    this.showNoteOrNot();
                    if (this.reportModel.translationCurrencyCodes) {
                        const codes = this.reportModel.translationCurrencyCodes;
                        this.translationCurrencies = codes.length > 2 ? `${codes[0]}, ${codes[1]} + ${codes.length - 2} more` :
                            codes.join(', ');
                    }
                    if (this.reportModel.reportingCurrencyCodes) {
                        const codes = this.reportModel.reportingCurrencyCodes;
                        this.reportingCurrencies = codes.length > 2 ? `${codes[0]}, ${codes[1]} + ${codes.length - 2} more` :
                            codes.join(', ');
                    }
                    console.log('[FxRate] Before Update', this.reportModel.dates);
                    this.fixDates(this.reportModel);
                    console.log('[FxRate] After Update', this.reportModel.dates);

                  this.platformSidebarStore.dispatch(new SetActivityFeedAction({type: 'ReferenceCode', 'id': `${this.reportModel.id}`, 'title': this.reportModel.reportName, isDefault: false}));
                }
            }
        });
    }

    invalidate(rateView: string) {
        const config = Configuration.getSmartGridConfig(this.sourceType);
        config.SourceType = this.newSourceType;
        config.Parameters['REPORT_ID'] = this.reportId;
        config.Parameters['RATE_VIEW'] = rateView;
        Configuration.setSmartGridConfig(config.SourceType, config);
        this.store.dispatch(new GetFieldSectionsAction(this.newSourceType));
        this.invalidateSubject.invalidate();
    }

    reportSettings() {
        this.router.navigateByUrl(`itrce/fxrates/add-report/${this.reportId}`);
    }

    exportReport() {
        this.fuseSplashScreenService.show();
        const command = new ExportReportCommand();
        command.reportId = this.reportId;
        this.commandService.executeCommand(command).pipe().subscribe(state => {
            if (state) {
                console.log(state);
                this.fileManagementService.downloadClientFileWithId(state.Payload).pipe().subscribe((payload: any) => {
                    this.fuseSplashScreenService.hide();
                    this.fileManagementService.openSaveFileWindow(payload, 'FxReport.xlsx');
                }, (error) => {
                    this.fuseSplashScreenService.hide();
                });
            }
        }, (error) => {
            this.fuseSplashScreenService.hide();
        });
    }

    onGridCellValueChange(event) {

    }

    closeNote() {
        this.showNote = false;
    }

    showNoteOrNot() {
        if (this.reportModel.detailRateView === 'Month-End') {
            this.showNote = false;
            return;
        }
        if (this.reportModel.rateType === FxRateRateType.TaxYearRate) {
            this.showNote = true;
            return;
        }
        if (this.reportModel.rateType === FxRateRateType.PeriodRate) {
            const startDate = new Date(this.reportModel.dates[0]);
            const endDate = new Date(this.reportModel.dates[1]);
            const dateLimit = new Date((startDate.getTime() + (1000 * 60 * 60 * 24 * 90)));
            if (endDate > dateLimit) {
                this.showNote = true;
            }
        }
    }

    goToEditPage() {
        this.router.navigateByUrl(`itrce/fxrates/add-report/${this.reportId}`);
    }

    rateViewChange(rateView: string) {
        const command = new ChangeSummaryHeaderCommand();
        command.sourceType = this.newSourceType;
        command.rateView = rateView;
        this.commandService.executeCommand(command).pipe().subscribe(state => {
            if (state) {
                this.invalidate(rateView);
            }
        });

    }

    ngOnDestroy(): void {
        this.store.dispatch(new ResetVirtualGridAction(this.newSourceType));
        const command = new DeleteDynamicFieldCommand();
        command.reportId = this.reportId;
        command.sourceType = this.newSourceType;
        this.commandService.executeCommand(command).pipe().subscribe(state => {
        });
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

  private fixDates(reportModel: FxRateReportModel) {
    if (!reportModel.isDateFixed) {
      for (let i = 0; i < reportModel.dates.length; i++) {
        reportModel.dates[i] = this.timeZoneService.addTimezoneOffsetToUTCDate(reportModel.dates[i]);
      }
      reportModel.isDateFixed = true;
    }
  }
}
